"use client";

import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Bell, BellOff, Check, CheckCheck, MailOpen, MoreHorizontal, Settings, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useNotifications } from "@/context/notification-context";
import { useAdminNotifications } from "@/context/admin-notification-context";
import { useSession } from "next-auth/react";
import { getNotificationUrl as resolveNotificationUrl } from "@/lib/notification-urls";



interface NotificationDropdownProps {
  className?: string;
}

function safeFormatDistanceToNow(dateString: string | undefined | null, fallback = "-") {
  if (!dateString) return fallback;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return fallback;
  return formatDistanceToNow(date, { addSuffix: true });
}

const GROUPABLE_TYPES = new Set(["POST_LIKED", "VIDEO_LIKED", "FORUM_REPLY", "NEW_FOLLOWER"]);

async function persistAdminReadIds(ids: Set<string>) {
  await fetch("/api/user/preferences", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      notificationPreferences: { adminDropdownReadIds: Array.from(ids) },
    }),
  }).catch(() => {});
}

export function NotificationDropdown({ className }: NotificationDropdownProps): JSX.Element {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [readAdminIds, setReadAdminIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  
  const {
    notifications,
    unreadCount,
    isLoading: loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    markAsUnread,
  } = useNotifications();

  // Get admin notifications if user is admin
  const adminNotifications = useAdminNotifications();
  const status = useSession()?.status;

  // Load admin read IDs from DB on mount
  useEffect(() => {
    if (status !== "authenticated") return;
    fetch("/api/user/preferences")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const saved = data?.notificationPreferences?.adminDropdownReadIds;
        if (Array.isArray(saved)) setReadAdminIds(new Set(saved as string[]));
      })
      .catch(() => {});
  }, [status]);

  // Prune IDs that are no longer in the pending list (once loaded)
  useEffect(() => {
    if (adminNotifications.isLoading) return;
    const currentIds = new Set(adminNotifications.pendingItems.map(n => n.id));
    setReadAdminIds(prev => {
      const pruned = new Set([...prev].filter(id => currentIds.has(id)));
      if (pruned.size !== prev.size) persistAdminReadIds(pruned);
      return pruned;
    });
  }, [adminNotifications.pendingItems, adminNotifications.isLoading]);

  const markAdminNotifAsRead = useCallback((id: string) => {
    setReadAdminIds(prev => {
      const next = new Set([...prev, id]);
      persistAdminReadIds(next);
      return next;
    });
  }, []);

  // Every pending admin-queue item (PENDING content report, OPEN ticket, etc.)
  // is already mirrored to a real `Notification` row by `createAdminNotifications`,
  // so the same task can otherwise appear twice in the dropdown — once as a
  // personal notification (which carries the real read state and is what the
  // /notifications page shows) and once as a synthetic admin-queue item.
  // De-dupe by the underlying entity id (`data.itemId` on personal rows ==
  // `id` on admin-queue rows) and keep the personal copy, so the bell badge
  // matches the /notifications page exactly.
  const personalItemIds = useMemo(() => {
    const ids = new Set<string>();
    for (const n of notifications) {
      const id = (n as any).data?.itemId;
      if (typeof id === "string" && id) ids.add(id);
    }
    return ids;
  }, [notifications]);

  // Combine personal and (deduped) admin notifications
  const allNotifications = useMemo(() => {
    const personalNotifications = notifications.map(n => ({
      ...n,
      isAdminNotification: false,
      priority: 'personal'
    }));

    const adminNotificationsList = adminNotifications.pendingItems
      .filter(n => !personalItemIds.has(n.id))
      .map(n => ({
        id: n.id,
        type: 'ADMIN_TASK',
        title: n.title,
        message: n.message,
        createdAt: n.createdAt,
        read: readAdminIds.has(n.id),
        data: {
          ...n.data,
          isAdminNotification: true
        },
        isAdminNotification: true,
        priority: 'admin'
      }));

    // Sort all notifications by creation date (newest first)
    return [...adminNotificationsList, ...personalNotifications]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [notifications, adminNotifications.pendingItems, readAdminIds, personalItemIds]);

  // Calculate total unread count — dedupe matches `allNotifications` so the
  // badge can never exceed what the dropdown (or /notifications page) shows.
  const totalUnreadCount = useMemo(() => {
    const personalUnread = unreadCount;
    const adminUnread = adminNotifications.pendingItems.filter(
      n => !readAdminIds.has(n.id) && !personalItemIds.has(n.id),
    ).length;
    return personalUnread + adminUnread;
  }, [unreadCount, adminNotifications.pendingItems, readAdminIds, personalItemIds]);


  // Get notification icon based on type
  const getNotificationIcon = (type: string): string => {
    switch (type) {
      // Messages
      case "NEW_MESSAGE":
        return "💬";
      // Bookings / sessions
      case "BOOKING_REQUEST":
        return "📬";
      case "BOOKING_CONFIRMED":
        return "📅";
      case "BOOKING_CANCELLED":
        return "🚫";
      case "BOOKING_RESCHEDULE_PROPOSED":
        return "🔄";
      case "BOOKING_RESCHEDULE_RESPONSE":
        return "📅";
      case "SESSION_NO_SHOW":
        return "🚫";
      case "SESSION_REMINDER":
        return "⏰";
      // Certifications
      case "CERTIFICATION_APPROVED":
        return "🎓";
      case "CERTIFICATION_REJECTED":
        return "❌";
      // Video / social engagement
      case "VIDEO_PUBLISHED":
      case "VIDEO_UPLOADED":
        return "🎬";
      case "VIDEO_LIKED":
        return "❤️";
      case "VIDEO_COMMENTED":
        return "💬";
      case "NEW_FOLLOWER":
        return "👤";
      case "POST_LIKED":
        return "❤️";
      case "POST_COMMENTED":
        return "💬";
      // Study groups
      case "STUDY_GROUP_INVITE":
        return "📩";
      case "STUDY_GROUP_MEMBER_JOINED":
        return "👥";
      case "STUDY_GROUP_MEMBER_LEFT":
        return "👋";
      case "STUDY_GROUP_UPDATE":
        return "📣";
      // Forums
      case "FORUM_REPLY":
      case "FORUM_MENTION":
        return "💭";
      // Moderation / account
      case "MEMBER_REMOVAL_APPEAL":
        return "⚖️";
      case "SYSTEM_ANNOUNCEMENT":
        return "📢";
      case "ACCOUNT_WARNING":
        return "⚠️";
      case "ACCOUNT_SUSPENSION":
        return "🔒";
      // Payments
      case "PAYMENT_SUCCESS":
        return "💳";
      case "PAYMENT_FAILED":
        return "❌";
      case "PAYMENT_REQUIRES_ACTION":
        return "⚠️";
      case "REFUND_FAILED":
        return "↩️";
      // Stripe disputes / chargebacks
      case "DISPUTE_OPENED":
        return "⚖️";
      case "DISPUTE_CLOSED":
        return "✅";
      case "EARLY_FRAUD_WARNING":
        return "🚨";
      // Tutor payouts (Stripe Connect)
      case "PAYOUT_FAILED":
        return "🏦";
      case "CONNECT_ACCOUNT_NEEDS_ATTENTION":
      case "CONNECT_CAPABILITY_INACTIVE":
        return "⚠️";
      case "CONNECT_ACCOUNT_DEAUTHORIZED":
        return "🔌";
      // Subscriptions
      case "SUBSCRIPTION_TRIAL_ENDING":
        return "⏰";
      case "SUBSCRIPTION_PAYMENT_ACTION_REQUIRED":
        return "⚠️";
      case "SUBSCRIPTION_UPCOMING":
        return "📅";
      // Advertiser
      case "AD_APPLICATION_RECEIVED":
        return "📋";
      case "AD_APPLICATION_APPROVED":
        return "✅";
      case "AD_APPLICATION_REJECTED":
        return "❌";
      case "AD_CAMPAIGN_APPROVED":
        return "🚀";
      case "AD_CAMPAIGN_LIVE":
        return "📡";
      case "AD_CAMPAIGN_PAYMENT_FAILED":
        return "💳";
      case "AD_CAMPAIGN_REJECTED":
        return "🚫";
      case "AD_CAMPAIGN_PAUSED":
        return "⏸️";
      // Admin
      case "ADMIN_TASK":
        return "🔧";
      default:
        return "🔔";
    }
  };

  const getNotificationUrl = (notification: Record<string, any>): string => {
    return resolveNotificationUrl(notification.type, notification.data);
  };

  // Handle notification click
  const handleNotificationClick = (notification: Record<string, any>): void => {
    if (!notification.read) {
      if (notification.isAdminNotification) {
        markAdminNotifAsRead(notification.id);
      } else {
        markAsRead(notification.id);
      }
    }

    setIsOpen(false);

    const url = getNotificationUrl(notification);

    if (url && url !== "#") {
      router.push(url);
    }
  };

  // Handle mark all as read with toast
  const handleMarkAllAsRead = async (): Promise<void> => {
    try {
      const allAdminIds = adminNotifications.pendingItems.map(n => n.id);
      const next = new Set(allAdminIds);
      persistAdminReadIds(next);
      setReadAdminIds(next);
      await markAllAsRead();
      toast({ title: "Success", description: "All notifications marked as read" });
    } catch {
      toast({ title: "Error", description: "Failed to mark notifications as read", variant: "destructive" });
    }
  };

  // Display all notifications, with grouping for repeated social actions
  const displayNotifications = useMemo(() => {
    const base = allNotifications.filter((n) => n.title?.trim() && n.message?.trim());
    const grouped: Record<string, any> = {};
    const result: any[] = [];
    for (const n of base) {
      const targetId: string | undefined =
        n.data?.postId || n.data?.videoId || n.data?.topicId;
      if (GROUPABLE_TYPES.has(n.type) && targetId) {
        const key = `${n.type}__${targetId}`;
        if (grouped[key]) {
          grouped[key]._groupCount = (grouped[key]._groupCount || 1) + 1;
          if (!n.read) grouped[key].read = false;
        } else {
          grouped[key] = { ...n, _groupCount: 1 };
          result.push(grouped[key]);
        }
      } else {
        result.push(n);
      }
    }
    return result;
  }, [allNotifications]);

  const getGroupedTitle = (n: any): string => {
    if (!n._groupCount || n._groupCount <= 1) return n.title;
    const count = n._groupCount;
    switch (n.type) {
      case "POST_LIKED": return `${count} people liked your post`;
      case "VIDEO_LIKED": return `${count} people liked your video`;
      case "FORUM_REPLY": return `${count} new replies in your discussion`;
      case "NEW_FOLLOWER": return `${count} new followers`;
      default: return n.title;
    }
  };
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative rounded-full touch-manipulation", className)}
        >
          {totalUnreadCount > 0 ? (
            <Bell className="h-5 w-5" />
          ) : (
            <BellOff className="h-5 w-5" />
          )}
          {totalUnreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
            </Badge>
          )}
          <span className="sr-only">
            {totalUnreadCount > 0 ? `${totalUnreadCount} unread notifications` : "Notifications"}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-80 md:w-80 w-72 max-w-[95vw] flex flex-col overflow-hidden overflow-y-hidden p-0"
        sideOffset={8}
      >
        <div className="flex items-center justify-between p-2 shrink-0">
          <DropdownMenuLabel className="p-0 font-semibold">
            Notifications
          </DropdownMenuLabel>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={totalUnreadCount === 0}
              className="h-8 px-2 text-xs"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              asChild
            >
              <Link href="/settings?tab=notifications">
                <Settings className="h-3 w-3" />
              </Link>
            </Button>
          </div>
        </div>

        <DropdownMenuSeparator />

        <ScrollArea className="max-h-80">
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : displayNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-6 text-center">
              <Bell className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No notifications</p>
              <p className="text-xs text-muted-foreground">
                You&apos;re all caught up!
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {displayNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "relative group",
                    !notification.read && "bg-primary/5"
                  )}
                >
                  <DropdownMenuItem
                    className="flex items-start gap-3 p-3 cursor-pointer focus:bg-accent"
                    asChild
                  >
                    <Link
                      href={getNotificationUrl(notification)}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex-shrink-0 text-lg">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn(
                            "text-sm line-clamp-1",
                            notification.read ? "font-normal text-muted-foreground" : "font-semibold text-foreground"
                          )}>
                            {getGroupedTitle(notification)}
                          </p>
                          {!notification.read && (
                            <span className="mt-1 h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {safeFormatDistanceToNow(notification.createdAt)}
                        </p>
                      </div>
                    </Link>
                  </DropdownMenuItem>

                  {/* Action buttons */}
                  <div className="absolute top-2 right-2 opacity-70 hover:opacity-100 group-hover:opacity-100">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        {!notification.read && (
                          <DropdownMenuItem
                            onClick={() => {
                              if (notification.isAdminNotification) {
                                markAdminNotifAsRead(notification.id);
                              } else {
                                markAsRead(notification.id);
                              }
                            }}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Mark as read
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => {
                            deleteNotification(notification.id);
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                        {notification.read && (
                          <DropdownMenuItem
                            onClick={() => markAsUnread(notification.id)}
                          >
                            <MailOpen className="h-4 w-4 mr-2" />
                            Mark as unread
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {displayNotifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs"
                asChild
              >
                <Link href="/notifications" onClick={() => setIsOpen(false)}>
                  View all notifications
                </Link>
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 